import { useEffect, useState } from "react";
import { m } from "framer-motion";
import { MessageCircle, Phone, Star } from "lucide-react";
import { BRAND } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";

const NAV = [
  { label: "Rituals", href: "#services" },
  { label: "Sanctuary", href: "#sanctuary" },
  { label: "Reviews", href: "#reviews" },
  { label: "Visit Us", href: "#visit" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <m.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "glass-panel border-b border-magenta/15"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 lg:px-10">
        <button
          data-testid="header-logo-link"
          onClick={() => scrollToId("#top")}
          className="flex items-center gap-3"
          aria-label="Orchid Vue Spa — back to top"
        >
          <span className="grid h-11 w-11 place-items-center overflow-hidden rounded-full bg-cream shadow-[0_0_24px_rgba(217,38,148,0.35)]">
            <img
              src="/assets/logo-small.webp"
              alt="Orchid Vue Spa logo"
              className="h-9 w-9 object-contain"
            />
          </span>
          <span className="text-left leading-none">
            <span className="block font-serif text-lg font-semibold tracking-wide text-cream">
              ORCHID VUE
            </span>
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.3em] text-[#EF7CC2]">
              Spa · Bur Dubai
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToId(item.href);
              }}
              className="text-sm font-medium text-lavender transition-colors duration-300 hover:text-cream"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <span
            data-testid="header-rating-pill"
            className="hidden items-center gap-1.5 rounded-full border border-champagne/30 px-3 py-1.5 text-xs text-champagne md:flex"
          >
            <Star className="h-3.5 w-3.5 fill-champagne" />
            {BRAND.rating} · {BRAND.reviewCount} reviews
          </span>
          <a
            data-testid="header-call-button"
            href={BRAND.callLink}
            aria-label={`Call Orchid Vue Spa on ${BRAND.phoneDisplay}`}
            className="grid h-10 w-10 place-items-center rounded-full border border-champagne/40 text-champagne transition-all duration-300 hover:bg-champagne hover:text-plum-950 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2.5"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden text-sm font-semibold sm:inline">
              {BRAND.phoneDisplay}
            </span>
          </a>
          <a
            data-testid="header-whatsapp-button"
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with Orchid Vue Spa on WhatsApp"
            className="grid h-10 w-10 place-items-center rounded-full bg-wa text-plum-950 transition-transform duration-300 hover:scale-105 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2.5"
          >
            <MessageCircle className="h-4 w-4" />
            <span className="hidden text-sm font-semibold sm:inline">
              WhatsApp
            </span>
          </a>
        </div>
      </div>
    </m.header>
  );
}
